/**
 * App-level host for the Dex Live Scan menu (2026-09-23).
 *
 * The menu used to be rendered inside the Pokédex page, so every Live Scan
 * affordance elsewhere (the centre orb, the resume banner, the Profile CTA)
 * had to navigate to /pokedex and wait for that page, the heaviest in the
 * app, to mount before the sheet could paint: about ten seconds on a Galaxy
 * S21+ (owner report). Hosted here, next to the other route-independent
 * overlays, the sheet opens the instant it is requested and the page loads
 * underneath it. The Pokédex page keeps its totals fresh by listening for
 * the closed / confirmed window events instead of owning the callbacks.
 */
import { useEffect } from "react";
import { DexLiveScanMenu } from "@/components/DexLiveScanMenu";
import {
  DEX_SCAN_MENU_CLOSED_EVENT,
  DEX_SCAN_MENU_CONFIRMED_EVENT,
  closeDexLiveScanMenu,
  dexLiveScanMenuRequestedAt,
  useDexLiveScanMenuOpen,
} from "@/lib/dexLiveScanMenuState";
import { useLastActiveVariant } from "@/lib/dexStateStore";
import { installDexScanLogTee } from "@/lib/dexScanLog";

export function GlobalDexLiveScanMenu() {
  const open = useDexLiveScanMenuOpen();
  const variant = useLastActiveVariant() ?? "pokemon";

  // Open latency into the exported scan log: "pressing Live Scan takes 10 s
  // to open the menu" (owner, 2026-09-23) had no number behind it.
  useEffect(() => {
    if (!open) return;
    installDexScanLogTee();
    const requested = dexLiveScanMenuRequestedAt();
    // eslint-disable-next-line no-console
    console.log("[live-scan] menu opened", { afterMs: requested > 0 ? Date.now() - requested : null });
  }, [open]);

  return (
    <DexLiveScanMenu
      open={open}
      initialVariant={variant}
      onClose={() => {
        closeDexLiveScanMenu();
        if (typeof window !== "undefined") window.dispatchEvent(new Event(DEX_SCAN_MENU_CLOSED_EVENT));
      }}
      onConfirm={() => {
        if (typeof window !== "undefined") window.dispatchEvent(new Event(DEX_SCAN_MENU_CONFIRMED_EVENT));
      }}
    />
  );
}
